import { useState } from 'react';
import { deviceList } from '../releases';

/**
 * CHOOSING WHAT A RELEASE WENT ONTO.
 *
 * ONE COMPONENT BECAUSE IT IS ASKED IN TWO PLACES, and the second place is not
 * optional. A release reaches its devices over the following days — the Fold by
 * cable two days later, the S26 over the air overnight — so a picker that
 * existed only at the moment of cutting would be wrong about every release by
 * the next morning. It is asked when the release is recorded, and again on the
 * release itself afterwards.
 *
 * Two of these written separately would drift, and the way they would drift is
 * predictable: one of them would forget that a name typed by hand still needs a
 * way back out, and a typo would become permanent.
 *
 * THE OFFERED LIST IS THE PROJECT'S OWN HISTORY, never a built-in menu — see
 * knownDevices. Vinnie's four devices are called S25, S26, Pixel and Fold, and
 * no list shipped with the software could have guessed them.
 */
export default function DevicePicker({
  devices,
  offered,
  onChange,
  placeholder,
}: {
  /** What is currently chosen, in order. */
  devices: string[];
  /** Names this project has used before, likeliest first. */
  offered: string[];
  onChange: (devices: string[]) => void;
  placeholder?: string;
}) {
  const [adding, setAdding] = useState('');

  const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
  const has = (name: string) => devices.some((d) => same(d, name));
  const toggle = (name: string) =>
    onChange(has(name) ? devices.filter((d) => !same(d, name)) : [...devices, name]);

  const addTyped = () => {
    // deviceList does the trimming, the spacing and the duplicate check, so this
    // box cannot introduce a shape the rest of the app would have to cope with.
    const [name] = deviceList(adding);
    if (name && !has(name)) onChange([...devices, name]);
    setAdding('');
  };

  // Chosen names that are not among the offered ones — just typed, this time
  // round. They need their own row, or the only way to correct a typo is to
  // abandon whatever form this is sitting in.
  const typed = devices.filter((d) => !offered.some((o) => same(o, d)));

  return (
    <div className="device-pick">
      {offered.length > 0 && (
        <div className="cut-chips device-chips">
          {offered.map((name) => (
            <button
              key={name}
              type="button"
              className={`btn btn-small ${has(name) ? 'is-on' : ''}`}
              aria-pressed={has(name)}
              onClick={() => toggle(name)}
            >
              {name}
            </button>
          ))}
        </div>
      )}
      {typed.length > 0 && (
        <div className="cut-chips device-chips">
          {typed.map((name) => (
            <button
              key={name}
              type="button"
              className="btn btn-small is-on"
              aria-pressed
              title={`Remove ${name}`}
              onClick={() => toggle(name)}
            >
              {name} ×
            </button>
          ))}
        </div>
      )}
      <div className="cut-device-add">
        <input
          className="input"
          value={adding}
          /* Named the way somebody would say it out loud, and deliberately not
             a model number — the names people actually use are "the test phone"
             and "Reception's laptop", and an example full of model numbers
             teaches the wrong habit. */
          placeholder={placeholder ?? (offered.length ? 'Something else…' : 'e.g. the test phone, the office laptop')}
          aria-label="Add a device by name"
          onChange={(e) => setAdding(e.target.value)}
          /* Enter adds the name rather than submitting whatever form this sits
             in, which would record a release halfway through typing. */
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addTyped();
            }
          }}
        />
        <button type="button" className="btn btn-small" disabled={!adding.trim()} onClick={addTyped}>
          Add
        </button>
      </div>
    </div>
  );
}
